import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import {
  Button,
  ConfirmButton,
  EmptyIndicator,
  MoreOptions
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { CompetenciesEditor, CompetenciesProductBadge } from '.';

import type { CompetencyInProduct } from 'shared-types';
import type { UpdateCompetenciesInUserOrProduct } from 'shared-types/requests';
import type { UpdateProductCompetenciesRes } from 'shared-types/responses';

export type ProductCompetenciesType =
  | 'achievement'
  | 'calendar-event'
  | 'course'
  | 'path';

interface Props {
  productId: number;
  type: ProductCompetenciesType;
}

const pageStateKeyByType: Record<ProductCompetenciesType, string> = {
  achievement: 'achievement',
  'calendar-event': 'calendarEvent',
  course: 'course',
  path: 'path'
};

const competencyKey = (competency: CompetencyInProduct) =>
  `${competency.id}${competency.level ? `-${competency.level.id}` : ''}`;

const ProductCompetencies = ({ productId, type }: Props) => {
  const { dispatch } = useDispatch();

  const { messages, pageState } = useReduxState();

  const [pendingCompetencies, setPendingCompetencies] =
    useState<UpdateCompetenciesInUserOrProduct>(null);

  const pageStateKey = pageStateKeyByType[type];
  const product = pageState[pageStateKey];
  const competencies: CompetencyInProduct[] = product?.competencies || [];

  const submit = async (
    changes: UpdateCompetenciesInUserOrProduct,
    propagateToUsers = false
  ) => {
    try {
      const res = await api.post<UpdateProductCompetenciesRes>(
        `/admin/competencies/${type}/${productId}`,
        { ...changes, propagateToUsers }
      );
      if (!res.data?.success) return;

      dispatch.updatePageState({
        [pageStateKey]: { ...product, competencies: res.data.competencies }
      });
      dispatch.addNotice({
        type: 'success',
        text: messages.competenciesUpdatedSuccess
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  // the editor only offers competencies the product does not grant yet, so
  // everything it submits is an addition. removing is its own row action
  const handleEditorSubmit = async (
    added: { id: number; levelId: number | null }[]
  ) => {
    if (!added.length) return;

    // adding may also grant to users who already completed the product,
    // so that needs confirming before anything is saved
    setPendingCompetencies({ added, removed: [] });
  };

  const handleRemove = async (competency: CompetencyInProduct) => {
    await submit({
      added: [],
      removed: [{ id: competency.id, levelId: competency.level?.id || null }]
    });
    return true;
  };

  const handleConfirm = async (propagateToUsers: boolean) => {
    await submit(pendingCompetencies, propagateToUsers);
    setPendingCompetencies(null);
  };

  if (!productId) return null;

  return (
    <>
      <Box
        headerContent={
          <CompetenciesEditor
            buttonCategory="success"
            buttonIcon="plus"
            buttonSize="header"
            buttonText={messages.competenciesAdd}
            excludedCompetencies={competencies}
            modalActionText={messages.commonAdd}
            modalTitle={messages.competenciesAdd}
            onSubmit={handleEditorSubmit}
          />
        }
        padding={0}
        title={messages.competenciesGranted}
        titleDescription={messages.competenciesAddHelpText}
      >
        {competencies.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  {messages.commonName}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  shrink={true}
                  sticky={true}
                  stickyAnchor="right"
                />
              </BoxTableHeader>

              {competencies.map((competency) => (
                <BoxTableRow key={competencyKey(competency)}>
                  <BoxTableCell cellBreakpoint={breakpoints.medium}>
                    <CompetenciesProductBadge competency={competency} />
                  </BoxTableCell>

                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    shrink={true}
                    sticky={true}
                    stickyAnchor="right"
                    stickyBackgroundColor="white"
                  >
                    <MoreOptions renderInPortal={true}>
                      <ConfirmButton
                        actionText={messages.commonRemove}
                        buttonIcon="trash-alt"
                        buttonText={messages.commonRemove}
                        category="danger"
                        confirmText={messages.competenciesRemoveConfirm}
                        confirmTitle={messages.competenciesRemove}
                        onConfirm={() => handleRemove(competency)}
                      />
                    </MoreOptions>
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="medal" title={messages.commonNoCompetencies} />
        )}
      </Box>

      <ModalPortal>
        <Modal
          onHide={() => setPendingCompetencies(null)}
          title={messages.competenciesPropagateTitle}
          visible={Boolean(pendingCompetencies)}
        >
          <ModalContent>{messages.competenciesPropagateConfirm}</ModalContent>

          <ModalFooter>
            <Button
              category="light"
              onClick={() => handleConfirm(false)}
              text={messages.commonNo}
            />
            <Button
              category="success"
              onClick={() => handleConfirm(true)}
              text={messages.commonYes}
            />
          </ModalFooter>
        </Modal>
      </ModalPortal>
    </>
  );
};

export default ProductCompetencies;
